import * as React from 'react';
import { useCallback, useRef, useLayoutEffect, MutableRefObject } from 'react';
import {
    IDropdownProps,
    IDropdownStyles,
    ISelectableDroppableTextProps,
    IDropdown,
    IDropdownOption,
    IDropdownStyleProps,
    styled,
    RefObject,
    Dropdown,
} from 'office-ui-fabric-react';
import isFunction from 'lodash/isFunction';
import classNames from 'classnames';

import { useThemeState } from '../../../../../domain/theming';

import { getStyles } from './CustomListDropdown.styles';
import { CustomListDropdownMenuProps } from './CustomListDropdownMenu';
import { CustomListDropdownMenuProvider } from './CustomListDropdownMenuContext';
import { RTDDropdownOption } from './types';

type SelectableDropdownProps = ISelectableDroppableTextProps<IDropdown, HTMLDivElement>;

export interface CustomListDropdownBaseProps extends Omit<IDropdownProps, 'componentRef'> {
    componentRef?: React.RefObject<IDropdown> | RefObject<IDropdown> | ((ref: IDropdown | null) => void);

    onRenderHeaderPrefix?: () => JSX.Element | null;
    onRenderMenu: (props: CustomListDropdownMenuProps) => JSX.Element | null;

    options: RTDDropdownOption[];

    defaultSelectedKey?: string;
    defaultSelectedKeys?: string[];
    selectedKey?: string | null;
    selectedKeys?: string[] | null;
}

export interface CustomListDropdownProps
    extends Omit<CustomListDropdownBaseProps, 'onRenderMenu' | 'onRenderHeaderPrefix'> {
    onRenderHeader?: () => JSX.Element | null;
}

const CustomListDropdownBase: React.FC<CustomListDropdownBaseProps> = (props) => {
    const {
        componentRef: externalComponentRef,
        onRenderMenu,
        onRenderHeaderPrefix,
        onChange: externalOnChange,
        options,
        defaultSelectedKey,
        defaultSelectedKeys,
        selectedKey,
        selectedKeys,
        multiSelect,
    } = props;

    const theme = useThemeState();

    const dropdownRef = useRef<IDropdown | null>(null);
    const currentSelectedIndexes = useRef<Set<number>>();

    const componentRef = useCallback(
        (ref: IDropdown | null) => {
            // Support our internal ref, as well as possible props
            dropdownRef.current = ref;

            if (isFunction(externalComponentRef)) {
                externalComponentRef(ref);
            } else if (externalComponentRef) {
                (externalComponentRef as { current: IDropdown | null }).current = ref;
            }
        },
        [externalComponentRef]
    );

    const onChange = useCallback(
        (event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption | undefined, index?: number | undefined) => {
            externalOnChange?.(event, option, index);

            if (option === undefined || index === undefined) {
                return;
            }

            if (!currentSelectedIndexes.current || !multiSelect) {
                // Clear set if not multiselect
                currentSelectedIndexes.current = new Set();
            }

            if (option.selected || !multiSelect) {
                // Always add if not multiselect
                currentSelectedIndexes.current.add(index);
            } else {
                currentSelectedIndexes.current.delete(index);
            }
        },
        [externalOnChange, multiSelect]
    );

    const onRenderMenuList = useCallback(
        (innerProps?: SelectableDropdownProps, defaultRender?: (p: SelectableDropdownProps) => JSX.Element | null) => {
            const defaultList = innerProps && defaultRender?.(innerProps);

            // WARNING: This is very dangerous. In most circumstances, do not do this
            // We must do it here because the Dropdown API is lacking and doesn't provide defaultRender for onRenderItem.
            // Therefore we let the defaultRender for onRenderMenuList render the list, and grab the children from that render
            const children = defaultList?.props.children as JSX.Element[];

            return (
                <CustomListDropdownMenuProvider>
                    {onRenderMenu({
                        ...innerProps,
                        options: innerProps?.options,
                        multiSelect: multiSelect ?? false,
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        setSelectedIndex: (dropdownRef.current as any)?.setSelectedIndex,
                        onRenderHeaderPrefix,
                        ...(Array.isArray(children)
                            ? {
                                  renderedOptions: children,
                              }
                            : { defaultList }),
                        currentSelectedIndexes,
                        // Fabric allows number keys, but we do not (nor can this property be a number)
                        selectedKey: innerProps?.selectedKey as string | undefined | null,
                    })}
                </CustomListDropdownMenuProvider>
            );
        },
        [onRenderMenu, multiSelect, onRenderHeaderPrefix]
    );

    useLayoutEffect(() => {
        // Must be a layout effect to fire prior to the scrolling in SearchDropdownMenu, triggered via useEffect
        buildSelectedIndexes(defaultSelectedKeys, defaultSelectedKey, options, currentSelectedIndexes);
        // Default values are only updated on initial render
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useLayoutEffect(() => {
        // Must be a layout effect to fire prior to the scrolling in SearchDropdownMenu, triggered via useEffect
        buildSelectedIndexes(selectedKeys, selectedKey, options, currentSelectedIndexes);
    }, [selectedKeys, selectedKey, options]);

    return (
        <Dropdown
            {...props}
            calloutProps={{
                ...props.calloutProps,
                className: classNames(theme.classNames, props.calloutProps?.className),
            }}
            componentRef={componentRef}
            onRenderList={onRenderMenuList}
            onChange={onChange}
        />
    );
};

export const CustomListDropdown: React.FunctionComponent<CustomListDropdownBaseProps> = styled<
    CustomListDropdownBaseProps,
    IDropdownStyleProps,
    IDropdownStyles
>(CustomListDropdownBase, getStyles, undefined, {
    scope: 'CustomListDropdown',
});

const findSelectedIndexes = (selectedKeys: string[], options: RTDDropdownOption[]): Set<number> => {
    const set = new Set<number>();

    const keyToIndex: Record<string, number> = {};

    for (let i = 0; i < options.length; i++) {
        const option = options[i];
        keyToIndex[option.key] = i;
    }

    for (const key of selectedKeys) {
        const index = keyToIndex[key];

        if (index !== undefined) {
            set.add(index);
        }
    }

    return set;
};

const buildSelectedIndexes = (
    selectedKeys: string[] | null | undefined,
    selectedKey: string | null | undefined,
    options: RTDDropdownOption[],
    currentSelectedIndexes: MutableRefObject<Set<number> | undefined>
) => {
    if (selectedKeys === undefined && selectedKey === undefined) {
        return;
    }

    if (selectedKeys) {
        currentSelectedIndexes.current = findSelectedIndexes(selectedKeys, options);
    } else if (selectedKey) {
        currentSelectedIndexes.current = findSelectedIndexes([selectedKey], options);
    } else {
        currentSelectedIndexes.current = undefined;
    }
};
